import React from 'react';
import { useSwipeable } from 'react-swipeable';
import type { State, Type } from '../state';
import { useCarousel } from '../state';
import useIsomorphicEffect from '../../../../utilities/useIsomorphicEffect';
export interface MultiFrameItemsProps {
  children: React.ReactElement | React.ReactElement[];
  className?: string;
  customContainer?: string;
  itemWidth?: number;
  injectedIndex?: number;
  fullWidthMode?: boolean;
  disableSwiping?: boolean;
  partialNextSlideWidth?: number;
  onSwipe?: (state: State, type: Type) => void;
  role?: string;
  id?: string;
}

export const isPartialSlideMode = (partialWidth?: number) =>
  typeof partialWidth === 'number' && partialWidth > 0;

export const Items = ({
  children,
  className,
  customContainer,
  itemWidth = 200,
  injectedIndex,
  fullWidthMode = false,
  disableSwiping = false,
  partialNextSlideWidth,
  onSwipe,
  role,
  id,
}: MultiFrameItemsProps): React.ReactElement => {
  const [state, navigate] = useCarousel();
  const swipeableHandlers = useSwipeable({
    onSwipedLeft: /* istanbul ignore next */ () => navigate.next(onSwipe),
    onSwipedRight: /* istanbul ignore next */ () => navigate.previous(onSwipe),
    preventScrollOnSwipe: true, //stops the browser from scrolling while a user swipes
  });
  const swipeHandlers = disableSwiping ? {} : swipeableHandlers;

  const isValidIndex = (indexVal?: number) => {
    return (
      typeof indexVal === 'number' &&
      indexVal >= 0 &&
      indexVal < state.totalItems &&
      indexVal !== state.currentIndex
    );
  };

  useIsomorphicEffect(() => {
    if (isValidIndex(injectedIndex)) {
      navigate.current(injectedIndex as number);
    }
  }, [injectedIndex]);

  const leftVal = `${0 - state.currentIndex * itemWidth}px`;
  let widthVal = `${itemWidth * state.totalItems}px`;
  let transVal = `translateX(${leftVal})`;

  if (fullWidthMode || isPartialSlideMode(partialNextSlideWidth)) {
    widthVal = `${state.totalItems * 100}%`;
    transVal = `translateX(${
      0 - (state.currentIndex * 100) / state.totalItems
    }%)`;
  }

  return (
    <div style={{ overflow: 'hidden' }} className={customContainer} id={id}>
      <ul
        className={className}
        style={{
          padding: 0,
          margin: 0,
          listStyle: 'none',
          display: 'flex',
          width: widthVal,
          transform: transVal,
          transition: `transform ${state.speed}ms ease-in-out`,
        }}
        role={role}
        {...swipeHandlers}
      >
        {React.Children.map(children, (child, i) => {
          return React.cloneElement(child, {
            itemWidth,
            fullWidthMode,
            partialNextSlideWidth,
            index: i,
          });
        })}
      </ul>
    </div>
  );
};

Items.displayName = 'CarouselItems';
export default Items;
